import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useLanguage } from "../contexts/LanguageContext";

const SECTIONS = [
  {
    id: "en",
    label: "English",
    title: "About Chesshood",
    intro:
      "Chesshood is a place dedicated to teaching, learning, and improving at chess. It is for everyone who wants to learn and play: beginners, improvers, and experienced players alike.",
    body:
      "We build AI-powered chess training, practical learning tools, lessons, clubs, and camps. Chesshood was founded in 2016. Its founder is Lukasz Piecha, Vice President of the Hampshire Chess Association, Junior Chief of Coaches in the Hampshire Junior Chess Association, and a CSC tutor.",
    values:
      "We want chess improvement to feel clear, enjoyable, and accessible. We believe in a friendly culture, safe learning for children and families, and steady progress through better next moves.",
    bullets: [
      "For all ages: 5-150",
      "Personalized chess training",
      "Lessons, clubs, camps and community",
      "Friendly and safe environment",
    ],
    cta: "Follow on YouTube",
  },
  {
    id: "pl",
    label: "Polski",
    title: "O Chesshood",
    intro:
      "Chesshood to miejsce poswiecone nauce, treningowi i rozwijaniu umiejetnosci szachowych. Jest dla wszystkich, ktorzy chca uczyc sie i grac: poczatkujacych, rozwijajacych sie graczy i bardziej doswiadczonych szachistow.",
    body:
      "Tworzymy trening szachowy oparty na AI, praktyczne narzedzia do nauki, lekcje, kluby i obozy. Chesshood powstalo w 2016 roku. Zalozycielem jest Lukasz Piecha, Vice President of the Hampshire Chess Association, Junior Chief of Coaches w Hampshire Junior Chess Association oraz tutor CSC.",
    values:
      "Chcemy, aby rozwoj szachowy byl jasny, przyjemny i dostepny. Wierzymy w przyjazna atmosfere, bezpieczna nauke dla dzieci i rodzin oraz systematyczny postep dzieki lepszym kolejnym ruchom.",
    bullets: [
      "Dla wszystkich grup wiekowych: 5-150",
      "Spersonalizowany trening szachowy",
      "Lekcje, kluby, obozy i spolecznosc",
      "Przyjazne i bezpieczne srodowisko",
    ],
    cta: "Obserwuj na YouTube",
  },
  {
    id: "es",
    label: "Espanol",
    title: "Sobre Chesshood",
    intro:
      "Chesshood es un lugar dedicado a ensenar, aprender y mejorar en ajedrez. Es para todos los que quieren aprender y jugar: principiantes, jugadores en progreso y jugadores con experiencia.",
    body:
      "Creamos entrenamiento de ajedrez impulsado por IA, herramientas practicas de aprendizaje, clases, clubes y campamentos. Chesshood fue fundado en 2016. Su fundador es Lukasz Piecha, Vice President of the Hampshire Chess Association, Junior Chief of Coaches en la Hampshire Junior Chess Association y tutor de CSC.",
    values:
      "Queremos que la mejora en ajedrez sea clara, agradable y accesible. Creemos en una cultura amistosa, en un aprendizaje seguro para ninos y familias, y en el progreso constante a traves de mejores siguientes jugadas.",
    bullets: [
      "Para todas las edades: 5-150",
      "Entrenamiento de ajedrez personalizado",
      "Clases, clubes, campamentos y comunidad",
      "Entorno amigable y seguro",
    ],
    cta: "Seguir en YouTube",
  },
];

export default function AboutUs() {
  const { language } = useLanguage();
  const box = {
    background: "#0f0f0f",
    border: "1px solid #ff7a18",
    borderRadius: 12,
    color: "#e6e6e6",
    padding: 16,
    maxWidth: 1200,
    margin: "0 auto 24px",
  };

  const section = SECTIONS.find((entry) => entry.id === language) || SECTIONS[0];

  const title = {
    color: "#eaeaea",
    fontSize: "1.35rem",
    fontWeight: 800,
    margin: "6px 0 12px",
  };

  const desc = { fontSize: "1rem", lineHeight: 1.6, color: "#d2d2d2" };

  const imgStyle = {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: 10,
  };

  const stat = {
    background: "#141414",
    border: "1px solid #2a2a2a",
    borderRadius: 10,
    padding: "12px 14px",
    textAlign: "center",
    color: "#dcdcdc",
    height: "100%",
  };

  const sectionCard = {
    padding: "14px 16px",
    borderRadius: 14,
    background: "#131313",
    border: "1px solid #2a2a2a",
    marginBottom: 14,
  };

  const badge = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255, 122, 24, 0.14)",
    border: "1px solid rgba(255, 122, 24, 0.38)",
    color: "#ffb37b",
    fontSize: ".78rem",
    fontWeight: 800,
    marginBottom: 10,
  };

  return (
    <div style={box}>
      <Container fluid className="p-0">
        <h2 style={title}>About Chesshood</h2>

        <Row className="g-3 align-items-start">
          <Col xs={12} md={{ span: 5, order: 2 }}>
            <img
              src="abotus.png"
              alt="Chesshood training and community"
              style={imgStyle}
            />
            <div
              className="mt-3"
              style={{ fontSize: ".9rem", color: "#bcbcbc" }}
            >
              Chesshood for learners, families, clubs, and players of all ages.
            </div>
          </Col>

          <Col xs={12} md={{ span: 7, order: 1 }}>
            <div style={desc}>
              <section style={sectionCard}>
                <div style={badge}>{section.label}</div>
                <h3 style={{ color: "#eaeaea", fontSize: "1.1rem" }}>
                  {section.title}
                </h3>
                <p>{section.intro}</p>
                <p>{section.body}</p>
                <p>{section.values}</p>
                <ul style={{ paddingLeft: "1.1rem", marginBottom: 0 }}>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </section>

              <Row className="g-2 mt-2">
                <Col xs="auto">
                  <Button
                    variant="outline-warning"
                    href="https://www.youtube.com/channel/UCRH3CTUxnyKdKtxq31gGqxA"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {section.cta}
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="outline-light"
                    href="mailto:chesshood@chesshood.com"
                  >
                    chesshood@chesshood.com
                  </Button>
                </Col>
              </Row>

              <Row className="g-3 mt-3">
                <Col xs={12} sm={12} lg={12}>
                  <div style={stat}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                      Founded 2016
                    </div>
                    <div style={{ fontSize: ".9rem", color: "#bcbcbc" }}>
                      Chesshood community
                    </div>
                  </div>
                </Col>

                <Col xs={12} sm={6} lg={4}>
                  <div style={stat}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                      Junior Winchester Chess Club
                    </div>
                    <div style={{ fontSize: ".9rem", color: "#bcbcbc" }}>
                      Club founded in 2017
                    </div>
                  </div>
                </Col>

                <Col xs={12} sm={6} lg={4}>
                  <div style={stat}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                      AI Powered
                    </div>
                    <div style={{ fontSize: ".9rem", color: "#bcbcbc" }}>
                      Personalized training
                    </div>
                  </div>
                </Col>

                <Col xs={12} sm={6} lg={4}>
                  <div style={stat}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                      HCA
                    </div>
                    <div style={{ fontSize: ".9rem", color: "#bcbcbc" }}>
                      Hampshire Chess Association
                    </div>
                  </div>
                </Col>

                <Col xs={12} sm={6} lg={4}>
                  <div style={stat}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                      CSC
                    </div>
                    <div style={{ fontSize: ".9rem", color: "#bcbcbc" }}>
                      Chess in Schools and Communities
                    </div>
                  </div>
                </Col>

                <Col xs={12} sm={6} lg={4}>
                  <div style={stat}>
                    <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                      HJCA
                    </div>
                    <div style={{ fontSize: ".9rem", color: "#bcbcbc" }}>
                      Hampshire Junior Chess Association
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
